import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { getPipeline, getDeadlines, fmtValue, fmtDate } from "@/lib/api";
import { ScoreBadge } from "@/components/ScoreBadge";
import { DeadlineBadge } from "@/components/DeadlineBadge";
import { Building2, MapPin, AlertTriangle, ArrowRight, KanbanSquare } from "lucide-react";

const STATUS_COLORS = {
  Interested: "#2F6DA3",
  Bidding: "#FF8C00",
  Submitted: "#7C3AED",
  Won: "#16A34A",
  Lost: "#71717A",
};

export default function Pipeline() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: ["pipeline"], queryFn: getPipeline });
  const { data: deadlines } = useQuery({ queryKey: ["deadlines"], queryFn: () => getDeadlines(7) });

  const statuses = data?.statuses || ["Interested", "Bidding", "Submitted", "Won", "Lost"];
  const contracts = data?.contracts || [];
  const counts = data?.counts || {};

  const byStatus = (s) => contracts.filter((c) => c.pipeline_status === s);

  return (
    <div className="space-y-6">
      <div>
        <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#2F6DA3] mb-1">Track · Bid · Win</div>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter">Bid Pipeline</h1>
        <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
          Everything you're tracking, by stage. Set a contract's stage and owner from its detail page.
        </p>
      </div>

      {deadlines?.count > 0 && (
        <div className="border-2 border-[#C0392B] bg-red-50 p-4" data-testid="deadline-alert">
          <div className="flex items-center gap-2 font-bold text-[#C0392B]">
            <AlertTriangle size={16} /> {deadlines.count} tracked tender{deadlines.count > 1 ? "s" : ""} closing within 7 days
          </div>
          <div className="mt-2 space-y-1">
            {deadlines.contracts.map((c) => (
              <div key={c.id} onClick={() => navigate(`/contract/${c.id}`)} className="text-sm flex items-center gap-2 cursor-pointer hover:text-[#C0392B]" data-testid={`deadline-item-${c.id}`}>
                <DeadlineBadge deadline={c.deadline} />
                <span className="font-bold">{c.title}</span>
                <span className="text-xs text-zinc-500 font-mono">· {c.pipeline_owner || "Unassigned"}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="text-center py-20 font-mono text-sm text-zinc-400">Loading…</div>
      ) : contracts.length === 0 ? (
        <div className="border-2 border-dashed border-zinc-300 bg-white py-20 text-center" data-testid="pipeline-empty">
          <KanbanSquare size={32} className="mx-auto text-zinc-300 mb-3" />
          <p className="font-display text-2xl font-bold mb-2">Your pipeline is empty</p>
          <p className="text-sm text-zinc-500 mb-6">Open any opportunity and set its stage (Interested, Bidding, Submitted…) to start tracking it here.</p>
          <button onClick={() => navigate("/")} className="bg-[#14304F] text-white font-bold uppercase tracking-wider text-sm px-6 py-3" data-testid="go-opportunities">
            Browse Opportunities
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4" data-testid="pipeline-board">
          {statuses.map((s) => (
            <div key={s} className="border-2 border-zinc-950 bg-white" data-testid={`pipeline-col-${s.toLowerCase()}`}>
              <div className="px-3 py-2 border-b-2 border-zinc-950 flex items-center justify-between" style={{ background: STATUS_COLORS[s], color: "#fff" }}>
                <span className="font-display font-extrabold text-sm tracking-tight uppercase">{s}</span>
                <span className="text-xs font-mono bg-white/25 px-2 py-0.5">{counts[s] || 0}</span>
              </div>
              <div className="p-2 space-y-2 min-h-[120px]">
                {byStatus(s).map((c) => (
                  <div key={c.id} onClick={() => navigate(`/contract/${c.id}`)} data-testid={`pipeline-card-${c.id}`}
                    className="group border border-zinc-200 p-3 cursor-pointer hover:border-zinc-950 transition-colors">
                    <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
                      <ScoreBadge score={c.ai_score} />
                      <DeadlineBadge deadline={c.deadline} />
                    </div>
                    <div className="font-bold text-sm leading-tight group-hover:text-[#2F6DA3] transition-colors">{c.title}</div>
                    <div className="font-display text-lg font-extrabold mt-1">{fmtValue(c.value_amount)}</div>
                    <div className="flex flex-col gap-0.5 mt-1.5 text-[11px] text-zinc-500 font-mono">
                      <span className="flex items-center gap-1"><Building2 size={10} /> {c.buyer_name || "—"}</span>
                      <span className="flex items-center gap-1"><MapPin size={10} /> {c.matched_areas?.[0] || c.region || "UK"}</span>
                      <span>Owner: {c.pipeline_owner || "Unassigned"}</span>
                      <span>Closes {fmtDate(c.deadline)}</span>
                    </div>
                    {c.pipeline_notes && <div className="mt-1.5 text-[11px] text-zinc-600 italic border-t border-zinc-100 pt-1.5">{c.pipeline_notes}</div>}
                  </div>
                ))}
                {byStatus(s).length === 0 && <div className="text-[11px] text-zinc-300 font-mono text-center py-4">—</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
